import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setToken, type User } from '../lib/api';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  enterDemo: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(localStorage.getItem('iprep_demo') === 'true');

  useEffect(() => {
    if (isDemo) {
      setUser({ id: 0, name: 'Future Engineer', email: 'demo@etatooffer.local' });
      setLoading(false);
      return;
    }
    if (!localStorage.getItem('iprep_token')) {
      setLoading(false);
      return;
    }
    api.me().then(({ user: current }) => setUser(current)).catch(() => setToken(null)).finally(() => setLoading(false));
  }, [isDemo]);

  async function login(email: string, password: string) {
    const result = await api.login({ email, password });
    setToken(result.token);
    setUser(result.user);
    setIsDemo(false);
    localStorage.removeItem('iprep_demo');
  }

  async function register(name: string, email: string, password: string) {
    const result = await api.register({ name, email, password });
    setToken(result.token);
    setUser(result.user);
    setIsDemo(false);
    localStorage.removeItem('iprep_demo');
  }

  function enterDemo() {
    localStorage.setItem('iprep_demo', 'true');
    setIsDemo(true);
    setUser({ id: 0, name: 'Future Engineer', email: 'demo@etatooffer.local' });
  }

  function logout() {
    setToken(null);
    localStorage.removeItem('iprep_demo');
    setIsDemo(false);
    setUser(null);
  }

  return <AuthContext.Provider value={{ user, loading, isDemo, login, register, enterDemo, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
